import React from "react";
import { PanelRight } from "lucide-react";

import { useAppSelector } from "../../../app/store";

import { getAccountDetailByAccountNumber } from "../../creditAccounts/logic/slice";

import DPDPill from "./DPDPill";
import AssetClassificationPill from "./AssetClassificationPill";
import { CREDIT_FACILITY_LABELS } from "../../../../src/api/credit_accounts";
import { formatIndianCompactNumber } from "../../../lib/utils";

interface AccountSummaryProps {
  accountNumber: string;
  onViewDetailsClick: () => void;
}

const AccountSummary: React.FC<AccountSummaryProps> = ({
  accountNumber,
  onViewDetailsClick,
}) => {
  const accountDetails = useAppSelector((state) =>
    getAccountDetailByAccountNumber(state, accountNumber),
  );

  if (!accountDetails?.account) {
    return null;
  }

  const { account, accountOverview } = accountDetails;

  const extAccountNumber = account.ext_account_number;
  const product = account.product;
  const creditFacility = account.credit_facility;

  const sanctionedAmount = accountOverview?.credit_account_details?.original_sanction_amount;

  const outstandingBalance = accountOverview?.current_position?.outstanding_balance;

  const daysPastDue = accountOverview?.current_position?.days_past_due;
  const assetClassification = accountOverview?.current_position?.asset_classification;

  return (
    <div className="flex w-full flex-col gap-3 rounded-[8px] bg-[#F9FAFB] p-3">
      <div className="grid grid-cols-4 gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-[12px] font-medium leading-4 text-[#71717A]">
            Account Number
          </span>
          <span
            className="truncate text-[12px] font-medium leading-5 text-[#0A0A0A]"
            title={extAccountNumber}
          >
            {extAccountNumber || "--"}
          </span>
        </div>

        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-[12px] font-medium leading-4 text-[#71717A]">
            Product / Facility
          </span>
          <span
            className="truncate text-[12px] font-medium leading-5 text-[#0A0A0A]"
            title={`${product ?? ""}${
              creditFacility
                ? ` • ${CREDIT_FACILITY_LABELS[creditFacility]}`
                : ""
            }`}
          >
            {product || "--"}
            {creditFacility
              ? ` • ${CREDIT_FACILITY_LABELS[creditFacility]}`
              : ""}
          </span>
        </div>

        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-[12px] font-medium leading-4 text-[#71717A]">
            Original Sanctioned Amt. (in ₹)
          </span>
          <span className="text-[12px] font-medium leading-5 text-[#0A0A0A]">
            {sanctionedAmount !== null && sanctionedAmount !== undefined
              ? formatIndianCompactNumber(sanctionedAmount, {
                  includeCurrency: false,
                })
              : "--"}
          </span>
        </div>

        <div className="relative flex min-w-0 flex-col gap-1">
          <span className="text-[12px] font-medium leading-4 text-[#71717A]">
            Outstanding Amt. (in ₹)
          </span>
          <span className="text-[12px] font-medium leading-5 text-[#0A0A0A]">
            {outstandingBalance !== null && outstandingBalance !== undefined
              ? formatIndianCompactNumber(outstandingBalance, {
                  includeCurrency: false,
                })
              : "--"}
          </span>

          <button
            type="button"
            onClick={onViewDetailsClick}
            className="absolute right-0 top-0 flex items-center justify-center gap-2 rounded-[8px] bg-[#E5E7EB] px-2 py-1 text-xs font-medium leading-4 text-[#737373]"
          >
            <PanelRight size={16} />
            <span>View Details</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-[12px] font-medium leading-4 text-[#71717A]">
            DPD
          </span>
          <div className="flex items-center">
            <DPDPill value={daysPastDue} />
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-[12px] font-medium leading-4 text-[#71717A]">
            Asset Classification
          </span>
          <div className="flex items-center">
            <AssetClassificationPill
              value={assetClassification}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSummary;